/**
 * <ConfirmationCard /> — renders a parked chat mutation (M24).
 *
 * A chat tool that changes state never writes inline (commandment
 * 25). The orchestrator streams a `confirmation` event; this card
 * shows the before/after diff + an Apply button. Apply round-trips
 * `POST /api/chat/confirm`; only then does the change land. Cancel
 * dismisses it without writing.
 *
 * The card is terminal once resolved — applied shows the result
 * summary, cancelled is struck through, error offers a retry.
 */

import {
  AlertCircle,
  ArrowRight,
  Check,
  Loader2,
  ShieldCheck,
  X,
} from "lucide-react";

import { AIBadge } from "../ui/AIBadge";
import type { ChatConfirmation } from "./types";

function fmt(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export interface ConfirmationCardProps {
  confirmation: ChatConfirmation;
  onApply: () => void;
  onCancel: () => void;
}

export function ConfirmationCard({
  confirmation,
  onApply,
  onCancel,
}: ConfirmationCardProps) {
  const { preview, status } = confirmation;
  const keys = Array.from(
    new Set([
      ...Object.keys(preview.before),
      ...Object.keys(preview.after),
    ]),
  );
  const pending = status === "pending";
  const applying = status === "applying";

  return (
    <div className="rounded-lg border border-ai-subtle bg-ai-soft">
      <header className="flex items-center gap-1.5 border-b border-ai-subtle px-2.5 py-1.5">
        <AIBadge />
        <span className="text-xs font-semibold text-text-primary">
          {preview.action}
        </span>
        <span
          className={`ms-auto inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs uppercase ${
            preview.reversible
              ? "bg-emerald-50 text-emerald-700"
              : "bg-amber-50 text-amber-700"
          }`}
        >
          {preview.reversible && <ShieldCheck size={9} />}
          {preview.reversible ? "reversible" : "not reversible"}
        </span>
      </header>

      <div className="px-2.5 py-2">
        <p className="text-xs text-text-secondary">
          {preview.resource}
        </p>

        {/* before → after diff */}
        <dl className="mt-1.5 space-y-1">
          {keys.map((k) => (
            <div
              key={k}
              className="flex items-center gap-1.5 text-xs"
            >
              <dt className="min-w-[72px] text-text-tertiary">
                {k.replace(/_/g, " ")}
              </dt>
              <dd className="flex items-center gap-1 text-text-primary">
                <span className="rounded bg-surface-muted px-1 py-0.5 text-text-secondary line-through">
                  {fmt(preview.before[k])}
                </span>
                <ArrowRight
                  size={11}
                  className="shrink-0 text-text-tertiary"
                />
                <span className="rounded bg-emerald-50 px-1 py-0.5 font-medium text-emerald-700">
                  {fmt(preview.after[k])}
                </span>
              </dd>
            </div>
          ))}
        </dl>

        {preview.note && (
          <p className="mt-1.5 text-xs text-text-tertiary">
            {preview.note}
          </p>
        )}

        {status === "applied" && (
          <p className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700">
            <Check size={11} />
            {confirmation.resultSummary ?? "Applied."}
          </p>
        )}
        {status === "cancelled" && (
          <p className="mt-2 inline-flex items-center gap-1 text-xs text-text-tertiary">
            <X size={11} /> Cancelled — nothing was changed.
          </p>
        )}
        {status === "error" && (
          <p className="mt-2 inline-flex items-start gap-1 text-xs text-rose-700">
            <AlertCircle size={11} className="mt-px shrink-0" />
            {confirmation.errorMessage ?? "Couldn't apply this change."}
          </p>
        )}
      </div>

      {(pending || applying || status === "error") && (
        <footer className="flex items-center gap-1.5 border-t border-ai-subtle px-2.5 py-1.5">
          <button
            type="button"
            onClick={onApply}
            disabled={applying}
            className="inline-flex items-center gap-1 rounded bg-brand px-2 py-0.5 text-xs font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {applying ? (
              <Loader2 size={10} className="animate-spin" />
            ) : (
              <Check size={10} />
            )}
            {status === "error" ? "Retry" : "Apply"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={applying}
            className="inline-flex items-center gap-1 rounded border border-border-subtle px-2 py-0.5 text-xs text-text-secondary hover:bg-surface-muted disabled:opacity-50"
          >
            <X size={10} /> Cancel
          </button>
        </footer>
      )}
    </div>
  );
}
